import type {
    AdditionalDetailsData,
    CoreConfiguration,
    DropinConfiguration,
    ICore,
    PaymentCompletedData,
    PaymentFailedData,
    UIElement,
} from "@adyen/adyen-web/auto";
import { AdyenCheckout, Dropin } from "@adyen/adyen-web/auto";
import type { TOrderResponse, TPaymentResponse } from "@components/commerce/checkout/types";
import { activeSiteCountry } from "@config/site/site-config";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { Consents } from "@lib/constants/contentful/consent";
import { getPagePathByLocale, orderConfirmationPage } from "@lib/constants/pagePaths";
import { STORE_FRONT_CREATE_ORDER_ENDPOINT } from "@lib/constants/urlConstants";
import { FetchServiceAxios } from "@lib/helpers/fetchServiceAxios";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import type {
    CommercetoolsMoneyData,
    UseMoneyValue,
} from "@ui/components/data-display/money/useMoney";
import { colors, typography } from "@ui/design-tokens";
import type { NextRouter } from "next/router";
import { removeSessionIdAndRedirectResultFromUrl } from "../checkoutHelper";
import { getSessionIDForKount } from "../kount/session";
import type { OrderParams, OrderWithDeferredPaymentParams } from "./types";

export const getAdyenCheckout = ({
    router,
    money,
}: {
    router: NextRouter;
    money: UseMoneyValue;
}) => {
    return async ({
        paymentsResponse,
        node,
        dropinMicrocopy,
        sessionId,
        redirectResult,
        id,
        componentWasLoaded,
        isMobile,
        microcopyError,
        showErrorAndReload,
        showOrderCreationError,
        clearDropinError,
        scrollToTopOfPayment,
        showOrderCreation,
        enableLoader,
        disableLoader,
        showPaymentProcessingMessage,
        showOrderCreationMessage,
        showOnlyProgressBar,
        setShouldClearCart,
        getUtmParams,
        checkBeforeSubmit,
        clearCart,
    }) => {
        const { locale } = router;
        let checkout: ICore;
        let shouldScrollToBottom = false;
        const ddMessageOrderNotCreated = "PAYMENT: Order not created after successful payment!";
        const ddMessageOrderCreated = "PAYMENT: Order created";
        const isAdvancedFlow = process.env.NEXT_PUBLIC_PAYMENT_ADVANCED_FLOW === "true";

        if (componentWasLoaded.current) return;

        // Some styles for payment component we need to configure here before creating the component.
        // For example fontFamily, fontSize, etc. We can't override it in the css file.
        const paymentMethodStyles = {
            base: {
                fontFamily: typography.fonts.body,
                fontSmoothing: "antialiased",
                webkitFontSmoothing: "antialiased",
                fontSize: isMobile ? "14px" : "15px",
                fontWeight: "bold",
                padding: "14px 20px",
            },
            placeholder: {
                color: colors.gray[200],
                fontWeight: `${typography.fontWeights.normal}`,
            },
        };

        const dropinConfiguration: DropinConfiguration = {
            showRadioButton: true,
            paymentMethodsConfiguration: {
                card: {
                    styles: paymentMethodStyles,
                    holderNameRequired: true,
                    hasHolderName: true,
                    maskSecurityCode: true,
                },
                econtext_stores: {
                    personalDetailsRequired: false,
                },
                econtext_seven_eleven: {
                    personalDetailsRequired: false,
                },
            },
            onReady: async () => {
                shouldScrollToBottom = true;
            },
        };

        const basicConfig: CoreConfiguration = {
            clientKey: process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY,
            locale: locale,
            environment: process.env.NEXT_PUBLIC_ADYEN_ENVIRONMENT.toLowerCase() as any,
            countryCode: activeSiteCountry,
            translations: await getComponentTranslations(),
            showPayButton: true,
            analytics: {
                enabled: true,
            },
            amount: {
                value: (money?.original as CommercetoolsMoneyData)?.centAmount,
                currency: money?.currencyCode,
            },
            onPaymentCompleted: paymentAccepted,
            onError: async (error, component) => {
                logPaymentError("PAYMENT: onError event", error);

                removeSessionIdAndRedirectResultFromUrl();
                await showDropinError(component);
            },
            onChange: () => {
                if (shouldScrollToBottom) {
                    scrollToTopOfPayment();
                    shouldScrollToBottom = false;
                }
            },
        };

        if (isAdvancedFlow) {
            checkout = await AdyenCheckout({
                ...basicConfig,
                paymentMethodsResponse: paymentsResponse,
                onSubmit: payment,
                onAdditionalDetails: paymentDetails,
            });
        } else {
            checkout = await AdyenCheckout({
                ...basicConfig,
                session: await getSession(),
                onPaymentCompleted: paymentAccepted,
                onPaymentFailed: onPaymentFailed,
            });
        }

        async function mountCard(component?: any) {
            component && component.unmount();

            if (!componentWasLoaded.current) {
                if (redirectResult) {
                    removeSessionIdAndRedirectResultFromUrl();
                    checkout.submitDetails({ details: { redirectResult: redirectResult } });
                    scrollToTopOfPayment();
                } else {
                    createDropinComponent();
                    logPaymentInfo("PAYMENT: Payment component created");
                }
            }

            componentWasLoaded.current = true;
        }

        async function paymentAccepted(result: PaymentCompletedData, component: UIElement) {
            clearDropinError();
            if (!result || result?.resultCode !== "Authorised") {
                logPaymentError("PAYMENT: Unauthorised status after payment", result);

                removeSessionIdAndRedirectResultFromUrl();
                await showDropinError(component);

                return;
            }

            logPaymentInfo("PAYMENT: Payment Authorised by Adyen");

            showOrderCreation();

            let data: TOrderResponse = null;
            const startTime = Date.now();
            const longWaitMarkets = [
                "KR",
                "CA",
                "US",
                "AU",
                "JP",
                "PL",
                "BE",
                "CZ",
                "FR",
                "HU",
                "PT",
                "SK",
                "SI",
                "AT",
                "CH",
                "DE",
                "NL",
            ];
            const maxWaitTime = longWaitMarkets.some((market) => locale.includes(market)) // 60 seconds for KR, CA, US, AU, JP and 20 seconds for others
                ? 60000
                : 20000;

            //sometimes Adyen notification webhook is delivered after few seconds/minutes so we need to wait for order creation
            //to make sure that we have transactions in CT
            let retryCount = 0;
            const maxRetries = 3;
            const retryDelay = 2000;
            while (!data && Date.now() - startTime < maxWaitTime && retryCount < maxRetries) {
                data = await FetchServiceAxios.post<OrderParams, TOrderResponse>(
                    `/api/${STORE_FRONT_CREATE_ORDER_ENDPOINT}`,
                    {
                        kountSessionId: getSessionIDForKount(),
                        userAgent: navigator.userAgent,
                        locale,
                        countryCode: activeSiteCountry,
                        tracking: {
                            utm: getUtmParams(),
                        },
                        newsletterKey: Consents.newsletter,
                        privacyPolicyKey: Consents.privacyPolicy,
                        termsAndConditionsKey: Consents.termsAndConditions,
                    }
                )
                    .then(async (data) => {
                        switch (data?.resultCode) {
                            case "Authorised":
                                return data;
                            case "Failed":
                                return {
                                    resultCode: "PaymentFailed",
                                };
                            case "NoTransactionFound":
                            default:
                                return null;
                        }
                    })
                    .catch(async (error) => {
                        retryCount++;
                        if (retryCount < maxRetries) {
                            logOrderConfirmation(
                                "PAYMENT: Order creation failed. Retrying...",
                                error,
                                retryCount
                            );
                            await new Promise((resolve) => setTimeout(resolve, retryDelay));
                            return null;
                        } else {
                            logOrderConfirmation(
                                "PAYMENT: Order creation failed. Maximum retries reached...",
                                error,
                                retryCount
                            );
                            return error; // Add this line to return the error after maximum retries
                        }
                    });
            }

            if (data?.resultCode === "PaymentFailed") {
                logPaymentError("PAYMENT: Payment failed");
                await showDropinError(component);
                return;
            } else if (!data || data?.resultCode !== "Authorised" || !data?.orderId) {
                // if error in SFAPI or no transaction in CT
                logPaymentError(ddMessageOrderNotCreated, data);
                await showOrderCreationError(component);
                // We don't want to clear cart instantly, so we will handle it through a state and next router.events
                setShouldClearCart(true);

                return;
            }

            AnalyticsClient.purchaseStatus(data, "");
            onPaymentCompleted(data);
            logPaymentInfo(ddMessageOrderCreated);

            if (!!checkout && !!component) {
                await checkout?.remove(component);
            }
        }

        async function createOrderWithDeferredPayment(
            paymentMethod: string,
            referenceId: string,
            component: UIElement
        ) {
            if (paymentMethod !== "econtext_stores" && paymentMethod !== "econtext_seven_eleven") {
                return;
            }

            if (paymentMethod === "econtext_stores" && !referenceId) {
                logPaymentError("PAYMENT: ReferenceId is missing for convenience store payment");
                await showDropinError(component);
                return;
            }

            const data = await FetchServiceAxios.post<
                OrderWithDeferredPaymentParams,
                TOrderResponse
            >(`/api/payment/order-with-deferred-payments`, {
                paymentMethod: paymentMethod,
                referenceId: referenceId,
                newsletterKey: Consents.newsletter,
                privacyPolicyKey: Consents.privacyPolicy,
                termsAndConditionsKey: Consents.termsAndConditions,
                userAgent: navigator.userAgent,
                locale,
                countryCode: activeSiteCountry,
                tracking: {
                    utm: getUtmParams(),
                },
            }).catch(async (error) => {
                logOrderConfirmation(
                    "PAYMENT: Order creation failed with deferred payment",
                    error,
                    0
                );
                await showOrderCreationError();
            });

            if (!data || data?.resultCode !== "Authorised" || !data?.orderId) {
                logPaymentError(
                    "PAYMENT: Payment failed after creating order with deferred payment"
                );
                await showDropinError(component);
                return;
            }

            logPaymentInfo(ddMessageOrderCreated);

            AnalyticsClient.purchaseStatus(data, "");

            //7Eleven specification: we don't want to redirect user to order confirmation page
            if (paymentMethod === "econtext_seven_eleven") {
                clearCart();
                return;
            }

            onPaymentCompleted(data);

            showOnlyProgressBar();
        }

        async function onPaymentFailed(result: PaymentFailedData, component: UIElement) {
            logPaymentError("PAYMENT: Unauthorised status after payment", result);

            removeSessionIdAndRedirectResultFromUrl();
            await showDropinError(component);
        }

        async function payment(state, dropin) {
            logPaymentInfo("PAYMENT: Payment");

            clearDropinError();
            enableLoader();
            const isValid = await checkProductAvailability();
            if (!isValid) {
                logPaymentInfo("PAYMENT: Product is not available for payment in payment");
                return;
            }

            const { paymentMethod, browserInfo } = state.data;

            await FetchServiceAxios.post<any, TPaymentResponse>("api/payment/payment", {
                browserInfo: browserInfo,
                paymentMethod: {
                    type: paymentMethod.type,
                    encryptedCardNumber: paymentMethod.encryptedCardNumber,
                    encryptedExpiryMonth: paymentMethod.encryptedExpiryMonth,
                    encryptedExpiryYear: paymentMethod.encryptedExpiryYear,
                    encryptedSecurityCode: paymentMethod.encryptedSecurityCode,
                    holderName: paymentMethod.holderName,
                    subType: paymentMethod.subType,
                },
                kountSessionId: getSessionIDForKount(),
                userAgent: navigator.userAgent,
                countryCode: activeSiteCountry,
            })
                .then(async (data: TPaymentResponse) => {
                    logPaymentInfo(`PAYMENT: Payment response: ${data}`);

                    if (!data || (data?.resultCode !== "Authorised" && !data?.action)) {
                        logPaymentInfo(
                            `PAYMENT: Unauthorised status after payment with resultCode: ${data?.resultCode}`
                        );

                        setComponentStatusReady(dropin);
                        await showDropinError(dropin);
                        return;
                    }

                    if (data.action) {
                        try {
                            const deserializeAction = JSON.parse(data.action);

                            await createOrderWithDeferredPayment(
                                deserializeAction?.paymentMethodType,
                                deserializeAction?.reference,
                                dropin
                            );

                            dropin.handleAction(deserializeAction);
                        } catch (error) {
                            await showDropinError(dropin);
                            logPaymentError("PAYMENT: ERROR", error);
                        }
                    } else {
                        showOrderCreationMessage();
                        await createOrder();
                    }
                })
                .catch(async (error) => {
                    setComponentStatusReady(dropin);
                    await showDropinError(dropin);
                    logPaymentError("PAYMENT: ERROR", error);
                });
        }

        async function paymentDetails(state: AdditionalDetailsData, dropin: UIElement) {
            logPaymentInfo("PAYMENT: Payment details");

            clearDropinError();
            showPaymentProcessingMessage();
            const isValid = await checkProductAvailability();
            if (!isValid) {
                logPaymentInfo("PAYMENT: Product is not available for payment in payment details");
                return;
            }

            await FetchServiceAxios.post<any, TPaymentResponse>("api/payment/payment-details", {
                threeDSResult: state.data?.details?.threeDSResult,
                redirectResult: state.data?.details?.redirectResult,
                kountSessionId: getSessionIDForKount(),
                userAgent: navigator.userAgent,
                countryCode: activeSiteCountry,
            })
                .then(async (data) => {
                    logPaymentInfo(`PAYMENT: Payment details response: ${data}`);

                    if (!data || data?.resultCode !== "Authorised") {
                        logPaymentInfo(
                            `PAYMENT: Unauthorised status after payment with resultCode: ${data?.resultCode}`
                        );
                        reloadPaymentComponent();

                        componentWasLoaded.current = false;

                        await showDropinError(dropin);

                        return;
                    }
                    showOrderCreationMessage();
                    await createOrder();
                })
                .catch(async (error) => {
                    reloadPaymentComponent();
                    await showDropinError(dropin);
                    logPaymentError("PAYMENT: ERROR after 3DS2", error);
                });
        }

        async function createOrder() {
            logPaymentInfo("PAYMENT: Create order");

            const result: TOrderResponse = await FetchServiceAxios.post<any, TOrderResponse>(
                `api/${STORE_FRONT_CREATE_ORDER_ENDPOINT}`,
                {
                    kountSessionId: getSessionIDForKount(),
                    userAgent: navigator.userAgent,
                    countryCode: activeSiteCountry,
                    tracking: {
                        utm: getUtmParams(),
                    },
                    newsletterKey: Consents.newsletter,
                    privacyPolicyKey: Consents.privacyPolicy,
                    termsAndConditionsKey: Consents.termsAndConditions,
                }
            )
                .then((data) => {
                    switch (data?.resultCode) {
                        case "Authorised":
                            return data;
                        case "Failed":
                        case "NoTransactionFound":
                        default:
                            return null;
                    }
                })
                .catch((error) => {
                    logOrderConfirmation(
                        "PAYMENT: Order creation failed. Error posting to Create Order Endpoint",
                        error,
                        0
                    );
                    return error;
                });

            //what when we have fail like execption but what when we have fail like amount dismatch?
            if (!result || result?.resultCode !== "Authorised" || !result?.orderId) {
                logPaymentError(ddMessageOrderNotCreated, result);
                await showOrderCreationError();
                return;
            }

            AnalyticsClient.purchaseStatus(result, "");
            onPaymentCompleted(result);

            logPaymentInfo(ddMessageOrderCreated);
        }

        async function onPaymentCompleted(result: TOrderResponse) {
            if (process.env.NEXT_PUBLIC_INSIDER_TAG_ENABLED === "true") {
                sessionStorage.setItem("insider_order_confirmed", "true");
            }

            router.push(
                `${getPagePathByLocale(orderConfirmationPage, router.locale)}/${result.orderId}`
            );
        }

        function getSession() {
            if (sessionId && redirectResult) {
                return {
                    id: sessionId,
                };
            } else {
                return {
                    id: paymentsResponse?.paymentSession.id,
                    sessionData: paymentsResponse?.paymentSession.sessionData,
                };
            }
        }

        function getComponentTranslations() {
            try {
                return dropinMicrocopy?.trim() ? JSON.parse(dropinMicrocopy) : "";
            } catch (error) {
                logPaymentError("PAYMENT: Error during parsing translations", error);
                microcopyError();
            }
        }

        async function showDropinError(component?) {
            if (!!checkout && !!component && !isAdvancedFlow) {
                await checkout?.remove(component);
            }

            logPaymentInfo("PAYMENT: Show error message and reload payment component");
            removeSessionIdAndRedirectResultFromUrl();
            await showErrorAndReload();
        }

        async function checkProductAvailability() {
            const { isValid, action } = await checkBeforeSubmit();

            if (!isValid) {
                action();
                return false;
            }

            return true;
        }

        function setComponentStatusReady(component) {
            disableLoader();

            component?.setStatus("ready");
        }

        function reloadPaymentComponent() {
            disableLoader();
            componentWasLoaded.current = false;
            createDropinComponent();
        }

        function createDropinComponent() {
            new Dropin(checkout, dropinConfiguration).mount(node);
        }

        function logPaymentError(message: string, error?) {
            Logger.error(ServiceType.ADYEN, `${message} for cartId: ${id}`, {
                tag: LogTag.PAYMENT,
                cartId: id,
                error: {
                    name: error?.name,
                    message: error?.message,
                    stack: error?.stack,
                    ...error,
                },
            });
        }

        function logPaymentInfo(message: string) {
            Logger.info(ServiceType.ADYEN, `${message} for cartId: ${id}`, {
                tag: LogTag.PAYMENT,
                cartId: id,
            });
        }

        function logOrderConfirmation(message: string, error: any, retryCount: number) {
            Logger.error(ServiceType.NEXT_API, `${message} for cartId: ${id}`, {
                tag: LogTag.PAYMENT,
                cartId: id,
                error: error,
                retryCount: retryCount,
            });
        }

        mountCard();
    };
};
